import React, { useState, useEffect } from 'react';
import { Box, Switch } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import styles from '../../styles/component.module.scss';
import integrationAppStyles from './style.module.scss';
import PageHeader from '../Header/PageHeader';
import AppCard from '../AppCard';
import { fetchAppList, getAuthorizeUrl, revokeAppConnection } from '../../utils/api/app_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSnackbar } from '../../utils/useSnackbar';
import { setActivePage } from '../../features/navigation/navigationSlice';
import { userCanManageProjectSettings } from '../../utils/commonfunctions';
import DEFAULT_ROUTES from '../../constants/routes';

const USER_INTEGRATION_COPY = {
  pageTitle: 'App Integration ',
  pageDescription: 'Connect and manage third-party integrations',
  activePage: 'App Integration',
  breadcrumbProject: 'Project',
  unknownProject: 'Unknown Project',
  loading: 'Loading apps...',
  empty: 'No apps available for integration.',
  defaultDescription: 'No description available',
  scopeProject: 'Shared for this project',
  scopePersonal: 'Personal connection',
  connected: 'Connected',
  notConnected: 'Not Connected',
  connectedByPrefix: 'Connected by',
  selectProject: 'Please select a project first',
  fetchFail: 'Failed to fetch apps',
  disconnectSuccess: 'App disconnected successfully',
  disconnectFail: 'Failed to disconnect',
  authorizeFail: 'Failed to get authorization URL',
  toggleFail: 'Failed to toggle connection',
};

const UserIntegrationApp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activePage = useSelector((state) => state.navigation.activePage);
  const selectedProject = useSelector((state) => state.projects.selectedProject);
  const user = useSelector((state) => state.auth.user);
  const { showSnackbar } = useSnackbar();

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [togglingAppId, setTogglingAppId] = useState(null);

  const canManageSettings = userCanManageProjectSettings(user, selectedProject?.id);
  const visibleApps = apps.filter((app) => app.slug_name !== 'slack' || canManageSettings);

  useEffect(() => {
    dispatch(setActivePage(USER_INTEGRATION_COPY.activePage));
    fetchApps();
  }, [dispatch, selectedProject?.id]);

  const fetchApps = async () => {
    try {
      setLoading(true);
      const response = await fetchAppList({
        projectId: selectedProject?.id
      });

      if (response.status === STATUS.SUCCESS) {
        setApps(response.data || []);
      } else {
        showSnackbar(response.error || USER_INTEGRATION_COPY.fetchFail, 'error');
      }
    } catch (error) {
      console.error('Failed to fetch apps:', error);
      showSnackbar(USER_INTEGRATION_COPY.fetchFail, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleConnection = async (app, currentStatus) => {
    if (!selectedProject?.id) {
      showSnackbar(USER_INTEGRATION_COPY.selectProject, 'error');
      return;
    }

    try {
      setTogglingAppId(app.id);

      if (currentStatus) {
        const response = await revokeAppConnection({
          projectId: selectedProject.id,
          appId: app.id,
        });

        if (response.status === STATUS.SUCCESS) {
          showSnackbar(response.message || USER_INTEGRATION_COPY.disconnectSuccess, 'success');
          fetchApps();
        } else {
          showSnackbar(response.error || USER_INTEGRATION_COPY.disconnectFail, 'error');
        }
      } else {
        const response = await getAuthorizeUrl({
          projectId: selectedProject.id,
          appId: app.id,
        });

        if (response.status === STATUS.SUCCESS && response.data?.authorizeUrl) {
          window.open(response.data.authorizeUrl, '_blank');
        } else {
          showSnackbar(response.error || USER_INTEGRATION_COPY.authorizeFail, 'error');
        }
      }
    } catch (error) {
      console.error('Error toggling connection:', error);
      showSnackbar(USER_INTEGRATION_COPY.toggleFail, 'error');
    } finally {
      setTogglingAppId(null);
    }
  };

  const handleBreadcrumbClick = (id) => {
    if (id === 'home') {
      dispatch(setActivePage('Projects'));
      navigate(DEFAULT_ROUTES.BASE_URL);
    }
  };

  const breadcrumbItems = [
    { label: USER_INTEGRATION_COPY.breadcrumbProject, onClick: () => handleBreadcrumbClick('home') },
    { label: selectedProject?.name || USER_INTEGRATION_COPY.unknownProject },
    { label: activePage }
  ];

  return (
    <Box className={styles['page-container']}>
      <PageHeader
        projectName={selectedProject?.name || null}
        title={USER_INTEGRATION_COPY.pageTitle}
        description={USER_INTEGRATION_COPY.pageDescription}
        activePage={USER_INTEGRATION_COPY.activePage}
        breadcrumbItems={breadcrumbItems}
      />
      <Box
        className={`${styles['cover-container-col-auto']} ${integrationAppStyles.gridContainer}`}
      >
        <Box className={integrationAppStyles.cardContainer}>
          {loading ? (
            <Box className={integrationAppStyles.loadingState}>{USER_INTEGRATION_COPY.loading}</Box>
          ) : visibleApps.length === 0 ? (
            <Box className={integrationAppStyles.emptyState}>{USER_INTEGRATION_COPY.empty}</Box>
          ) : (
            visibleApps.map((app) => {
              const isConnected = app.is_connected === true;
              const isToggling = togglingAppId === app.id;
              const scopeLabel = app.connection_scope === 'project'
                ? USER_INTEGRATION_COPY.scopeProject
                : USER_INTEGRATION_COPY.scopePersonal;

              return (
                <AppCard
                  key={app.id}
                  header={app.name}
                  description={app.description || USER_INTEGRATION_COPY.defaultDescription}
                  icon={app.image_url}
                  showButton={true}
                  toggleButton={
                    <Switch
                      checked={isConnected}
                      onChange={() => handleToggleConnection(app, isConnected)}
                      disabled={isToggling || loading || !selectedProject?.id}
                    />
                  }
                >
                  <Box className={integrationAppStyles.statusContainer}>
                    <span className={integrationAppStyles.scopeLabel}>{scopeLabel}</span>
                    <span className={`${integrationAppStyles.statusText} ${isConnected ? integrationAppStyles.connected : integrationAppStyles.notConnected}`}>
                      {isConnected ? USER_INTEGRATION_COPY.connected : USER_INTEGRATION_COPY.notConnected}
                    </span>
                    {app.connected_by && (
                      <span className={integrationAppStyles.connectedBy}>
                        {USER_INTEGRATION_COPY.connectedByPrefix} {app.connected_by}
                      </span>
                    )}
                  </Box>
                </AppCard>
              );
            })
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default UserIntegrationApp;
